import {
  Component, ChangeDetectionStrategy, ElementRef, Injector, afterNextRender, computed, effect,
  HostListener, inject, signal, untracked, viewChild,
} from '@angular/core';
import {
  RouterOutlet, RouterLink, RouterLinkActive, Router, NavigationEnd, NavigationStart, IsActiveMatchOptions,
} from '@angular/router';
import { filter } from 'rxjs';

import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MsalService } from '@azure/msal-angular';
import { NotificationsService } from './core/services/notifications.service';
import { PropertiesService } from './core/services/properties.service';
import { IdentityService } from './core/services/identity.service';
import { UiLangService } from './core/services/ui-lang.service';
import { ShellBannerService, shellBannerActions } from './core/services/shell-banner.service';
import { ViewAsService } from './core/services/view-as.service';
import { Property, ExtranetRole } from './core/models/extranet.model';
import { NavItem, filterNav, navItemsFor } from './core/nav/nav-items';
import { computeScrollLeft } from './core/util/scroll';
import { BannerComponent } from './shared/banner.component';
import { ViewAsWriteDialogComponent } from './features/extranet/admin/shared/view-as-write-dialog.component';

/** Parent tabs light up on their route and everything under it (a booking's detail page keeps
 *  Reservations active); the tier-2 "All …" rows opt into exact matching via `NavChild.exact`. */
const SUBSET_MATCH: IsActiveMatchOptions = { paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' };
const EXACT_MATCH: IsActiveMatchOptions = { paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' };

@Component({
  selector: 'ext-root',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    OverlayModule,
    MatIconModule,
    MatButtonModule,
    TranslateModule,
    BannerComponent,
    ViewAsWriteDialogComponent
],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  private notifSvc = inject(NotificationsService);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private msal = inject(MsalService);
  private uiLangSvc = inject(UiLangService);
  private injector = inject(Injector);
  protected propSvc = inject(PropertiesService);
  protected identitySvc = inject(IdentityService);
  protected shellBannerSvc = inject(ShellBannerService);
  protected viewAs = inject(ViewAsService);

  properties = computed(() => this.propSvc.apiProperties());

  showShell = signal(AppComponent.inShell(this.router.url));

  /** URL after the last completed navigation — what the nav's own active-state maths reads. */
  private currentUrl = signal(this.router.url);
  /** Path (no query/fragment) of the last completed navigation, to tell a page change from a
   *  filter change on the same page (`?period=…`), which must not steal focus from the filter. */
  private lastPath = AppComponent.pathOf(this.router.url);

  private tabnav = viewChild<ElementRef<HTMLElement>>('tabnav');
  private mainEl = viewChild<ElementRef<HTMLElement>>('main');
  private tabScrollScheduled = false;

  constructor() {
    // Nothing ever activated a language before — every | translate pipe usage app-wide fell
    // back to showing the raw key (ngx-translate's default when no current lang is set).
    this.translate.setDefaultLang('en');
    this.uiLangSvc.init();

    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
      this.showShell.set(AppComponent.inShell(e.urlAfterRedirects));
      this.currentUrl.set(e.urlAfterRedirects);
      this.onNavigated(e);
    });
    // Close the topbar dropdowns on every navigation, not from the individual link's own click
    // handler — a click that triggers Router navigation (routerLink or a manual navigate() call)
    // unreliably also re-fires the ancestor's toggle-open (click) handler despite an intervening
    // stopPropagation(), reopening the panel right after it closes (reproduced live for both the
    // account and notification-bell dropdowns). Driving the close off the Router's own lifecycle
    // instead sidesteps that click-bubbling quirk entirely.
    this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe(() => {
      this.propertyDropdownOpen.set(false);
      this.accountDropdownOpen.set(false);
      this.bellDropdownOpen.set(false);
      this.openMenu.set(null);
    });
    // The active tab must stay fully visible in the scrolling strip: after every navigation, and
    // whenever the strip's content changes (the identity arriving swaps the nav, the shell
    // appearing mounts it). afterNextRender waits for routerLinkActive's own microtask + render.
    effect(() => {
      this.navItems(); this.currentUrl(); this.showShell();
      untracked(() => this.scheduleTabScroll());
    });
    // Load real properties + identity from API on startup
    this.propSvc.load();
    this.identitySvc.load();
  }

  get selectedProperty() { return this.propSvc.selectedProperty; }
  propertyDropdownOpen = signal(false);
  accountDropdownOpen = signal(false);
  bellDropdownOpen = signal(false);
  mobileDrawerOpen = signal(false);

  get unreadCount() {
    return this.notifSvc.unreadCount();
  }

  get recentNotifications() {
    return this.notifSvc.notifications().slice(0, 5);
  }

  get isAgencyManager() {
    return this.identitySvc.me()?.role === 'AGENCY_MANAGER';
  }

  get isAdmin() {
    return this.identitySvc.me()?.role === 'ADMIN';
  }

  /** Property selector and mobile property strip: hotel managers only. An agency works from
   *  contracts, not "inside" one property (handoff R9), and an admin has nothing to select. */
  readonly showPropertyChrome = computed(() => this.identitySvc.activeRole() === 'HOTEL_MANAGER');

  readonly shellBanner = this.shellBannerSvc.shellBanner;

  /** The shell banner's buttons (secondary first, main last) and their handlers by index —
   *  `ext-banner` renders the buttons, `actionClick` hands the index back. */
  readonly shellBannerActions = computed(() => shellBannerActions(this.shellBanner()));

  onShellBannerAction(index: number): void {
    this.shellBannerActions().handlers[index]?.();
  }

  /** The name the F9 write-mode confirmation addresses: the BE's own once GET /me answered as the group. */
  readonly viewAsGroupName = computed(() => this.identitySvc.me()?.viewAsGroupName || this.viewAs.group()?.name || '');

  // Dev-only "View as" role switcher (see IdentityService.switchRole). canSwitchRole() is only ever
  // true when the BE returns >1 availableRoles, which it does only with the switcher enabled on the
  // dev/local profile — so this control never appears on staging/production.
  private static readonly ROLE_LABELS: Record<string, string> = {
    ADMIN: 'topbar.roles.admin',
    AGENCY_MANAGER: 'topbar.roles.agencyManager',
    HOTEL_MANAGER: 'topbar.roles.hotelManager',
  };
  roleLabel(role: string) {
    const key = AppComponent.ROLE_LABELS[role];
    return key ? this.translate.instant(key) : role;
  }

  // Account dropdown's role badge — mirrors roleLabel()'s key lookup for the admin/hotel-manager
  // cases, but an agency member shows their real group name instead of a generic "Agency Manager".
  roleBadgeText(): string {
    if (this.isAdmin) return this.translate.instant('topbar.roles.admin');
    if (this.isAgencyManager) return this.identitySvc.me()?.groupName ?? '';
    return this.translate.instant('topbar.roles.hotelManager');
  }

  bellAriaLabel(): string {
    return this.unreadCount > 0
      ? this.translate.instant('topbar.notifications.unreadAriaLabel', { count: this.unreadCount })
      : this.translate.instant('topbar.notifications.title');
  }
  switchRole(role: ExtranetRole, e: Event) {
    e.stopPropagation();
    this.accountDropdownOpen.set(false);
    this.identitySvc.switchRole(role);
  }

  get greeting() {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  }

  // ---- Tier-2 nav ---------------------------------------------------------------------------

  /** The tabs for the active role (core/nav/nav-items.ts), less the page areas the member's
   *  profiles leave at NONE (Feature 4123). Until the identity lands: the hotel set, unfiltered —
   *  nothing is hidden before GET /me answers, as before. */
  navItems = computed<NavItem[]>(() => {
    const items = navItemsFor(this.identitySvc.activeRole());
    if (!this.identitySvc.me()) return items;
    return filterNav(items, area => this.identitySvc.levelOn(area));
  });

  readonly subsetMatch = SUBSET_MATCH;
  readonly exactMatch = EXACT_MATCH;

  /** Routes of the parents whose current page is one of their CHILDREN (Reservations while on
   *  /extranet/reports/reservations): the parent's own routerLinkActive can't see those, so the
   *  tab group carries this as a second class. */
  readonly activeParents = computed<ReadonlySet<string>>(() => {
    this.currentUrl();
    const on = new Set<string>();
    for (const item of this.navItems()) {
      for (const child of item.children ?? []) {
        if (this.router.isActive(child.route, child.exact ? EXACT_MATCH : SUBSET_MATCH)) { on.add(item.route); break; }
      }
    }
    return on;
  });

  /** Route of the parent whose submenu popover is open (one at a time), or null. */
  readonly openMenu = signal<string | null>(null);
  /** The chevron that opened the current popover — where focus returns on Escape. */
  private menuTrigger: HTMLElement | null = null;

  /** Under the trigger, start-aligned; flips above when there is no room (never off-screen). */
  readonly menuPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 2 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 2 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -2 },
  ];

  menuId(item: NavItem): string {
    return 'nav-menu-' + item.route.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '');
  }

  toggleMenu(item: NavItem, e: Event) {
    e.stopPropagation();
    if (this.openMenu() === item.route) { this.closeMenu(); return; }
    this.menuTrigger = e.currentTarget as HTMLElement;
    this.propertyDropdownOpen.set(false);
    this.accountDropdownOpen.set(false);
    this.bellDropdownOpen.set(false);
    this.openMenu.set(item.route);
  }

  /** Closes the open popover; `restoreFocus` hands focus back to its chevron (keyboard exits). */
  closeMenu(restoreFocus = false) {
    if (this.openMenu() === null) return;
    this.openMenu.set(null);
    if (restoreFocus) this.menuTrigger?.focus({ preventScroll: true });
  }

  /** WAI-ARIA menu button: ArrowDown/ArrowUp open the menu with the first/last row focused
   *  (Enter/Space are the button's native click, which opens it on the current page's row). */
  onTriggerKeydown(e: KeyboardEvent, item: NavItem) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    if (this.openMenu() !== item.route) this.toggleMenu(item, e);
    const last = e.key === 'ArrowUp';
    setTimeout(() => {
      const rows = this.menuRows();
      rows[last ? rows.length - 1 : 0]?.focus({ preventScroll: true });
    });
  }

  /** Focus lands on the current page's row, else the first — a screen reader hears where it is. */
  onMenuAttached() {
    setTimeout(() => {
      if (document.activeElement && this.menuRows().includes(document.activeElement as HTMLElement)) return;
      const rows = this.menuRows();
      (rows.find(r => r.classList.contains('active')) ?? rows[0])?.focus({ preventScroll: true });
    });
  }

  onMenuKeydown(e: KeyboardEvent) {
    const rows = this.menuRows();
    const idx = rows.indexOf(document.activeElement as HTMLElement);
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); rows[(idx + 1) % rows.length]?.focus({ preventScroll: true }); break;
      case 'ArrowUp': e.preventDefault(); rows[(idx - 1 + rows.length) % rows.length]?.focus({ preventScroll: true }); break;
      case 'Home': e.preventDefault(); rows[0]?.focus({ preventScroll: true }); break;
      case 'End': e.preventDefault(); rows[rows.length - 1]?.focus({ preventScroll: true }); break;
      case ' ': e.preventDefault(); (document.activeElement as HTMLElement | null)?.click(); break;
      case 'Escape': e.preventDefault(); e.stopPropagation(); this.closeMenu(true); break;
      // Tab leaves the menu: close it and continue from the chevron, so focus never lands at the
      // end of the document where the portalled popover lives.
      case 'Tab': this.closeMenu(true); break;
    }
  }

  private menuRows(): HTMLElement[] {
    const id = this.openMenu();
    if (id === null) return [];
    const menu = document.querySelector<HTMLElement>('.cdk-overlay-container .nav-menu');
    return menu ? Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]')) : [];
  }

  /** Nudges the strip's own scrollLeft so the active tab is fully visible (port of Shell.jsx).
   *  Never `scrollIntoView()`: that would also scroll the page under the sticky nav. */
  scrollActiveTabIntoView() {
    const nav = this.tabnav()?.nativeElement;
    if (!nav) return;
    const activeEl = nav.querySelector<HTMLElement>('.tab-group.active, .tab-item.active');
    if (!activeEl) return;
    // A parent tab is its label plus its chevron: bring the whole group in, not just the link.
    const active = activeEl.closest<HTMLElement>('.tab-group') ?? activeEl;
    const navRect = nav.getBoundingClientRect();
    const target = computeScrollLeft({ left: navRect.left, width: nav.clientWidth }, active.getBoundingClientRect(), nav.scrollLeft);
    if (target !== nav.scrollLeft) nav.scrollLeft = target;
  }

  private scheduleTabScroll() {
    if (this.tabScrollScheduled) return;
    this.tabScrollScheduled = true;
    afterNextRender(() => { this.tabScrollScheduled = false; this.scrollActiveTabIntoView(); }, { injector: this.injector });
  }

  @HostListener('window:resize')
  onResize() {
    this.scheduleTabScroll();
  }

  // ---- Skip link / focus on route change -----------------------------------------------------

  /** Skip link: focus the main landmark without a hash navigation (which would re-run the route). */
  skipToMain(e: Event) {
    e.preventDefault();
    this.focusMain(false);
  }

  private onNavigated(e: NavigationEnd) {
    const path = AppComponent.pathOf(e.urlAfterRedirects);
    const pageChanged = path !== this.lastPath;
    this.lastPath = path;
    // First navigation is the page load (id 1): focus stays where the browser put it. Later ones
    // that change the page move focus to <main>, so keyboard and screen-reader users continue
    // from the new content instead of from a tab that is no longer the one they are on.
    if (e.id > 1 && pageChanged && this.showShell()) {
      afterNextRender(() => this.focusMain(true), { injector: this.injector });
    }
  }

  private focusMain(preventScroll: boolean) {
    this.mainEl()?.nativeElement.focus({ preventScroll });
  }

  private static pathOf(url: string): string {
    return url.split(/[?#]/)[0];
  }

  /** The /extranet subtree only — not a look-alike such as /extranet-old, which is an unknown
   *  address outside it and shows "page not found" without the shell (WI 3787). */
  static inShell(url: string): boolean {
    return /^\/extranet(?:[/?#]|$)/.test(url);
  }

  // ---- Topbar dropdowns / drawer --------------------------------------------------------------

  togglePropertyDropdown(e: Event) {
    e.stopPropagation();
    this.propertyDropdownOpen.update(v => !v);
    this.accountDropdownOpen.set(false);
    this.bellDropdownOpen.set(false);
  }

  toggleAccountDropdown(e: Event) {
    e.stopPropagation();
    this.accountDropdownOpen.update(v => !v);
    this.propertyDropdownOpen.set(false);
    this.bellDropdownOpen.set(false);
  }

  toggleBellDropdown(e: Event) {
    e.stopPropagation();
    this.bellDropdownOpen.update(v => !v);
    this.propertyDropdownOpen.set(false);
    this.accountDropdownOpen.set(false);
  }

  markAllRead(e: Event) {
    e.stopPropagation();
    this.notifSvc.markAllRead();
  }

  openMobileDrawer() {
    this.mobileDrawerOpen.set(true);
    // The drawer is a modal surface: start keyboard users inside it, on its close button.
    afterNextRender(() => {
      document.querySelector<HTMLElement>('.mobile-drawer .icon-btn')?.focus({ preventScroll: true });
    }, { injector: this.injector });
  }

  closeMobileDrawer() {
    if (!this.mobileDrawerOpen()) return;
    this.mobileDrawerOpen.set(false);
    document.querySelector<HTMLElement>('.hamburger-btn')?.focus({ preventScroll: true });
  }

  selectProperty(prop: Property, e: Event) {
    e.stopPropagation();
    this.propSvc.select(prop);
    this.propertyDropdownOpen.set(false);
  }

  // Fallback photo for API properties that have no coverPhotoKey yet
  propPhoto(prop: Property): string {
    return 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=80&q=60';
  }

  @HostListener('document:click')
  closeDropdowns() {
    this.propertyDropdownOpen.set(false);
    this.accountDropdownOpen.set(false);
    this.bellDropdownOpen.set(false);
  }

  /** Escape closes whichever surface is open: the drawer first, then the topbar dropdowns. */
  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.mobileDrawerOpen()) { this.closeMobileDrawer(); return; }
    if (this.openMenu() !== null) { this.closeMenu(true); return; }
    this.closeDropdowns();
  }

  initials(name: string) {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  }

  signOut() {
    this.accountDropdownOpen.set(false);
    this.msal.logoutRedirect().subscribe();
  }
}
